import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useMutation, useQuery } from "@tanstack/react-query";
import CustomSelect from "src/components/custom-select";
import { getAllCommodityTypes, getServicesActive } from "src/queries/query";
import { toast } from "react-toastify";
import { FaSpinner } from "react-icons/fa";

const CommoditiesTable = ({
  values,
  stopIndex,
  commodities = [],
  onAddCommodity,
  onRemoveCommodity,
  onChangeCommodity,
  serviceUuid,
  onChangeService,
  classes = [
    "50",
    "55",
    "60",
    "65",
    "70",
    "77.5",
    "85",
    "92.5",
    "100",
    "110",
    "125",
    "150",
    "175",
    "200",
    "250",
    "300",
    "400",
    "500",
  ],
  descriptions = [],
  showServiceType = true,
  required = true,
  touched,
  errors,
  orderId,
}) => {
  const allDataCommodities =
    values?.orderStops?.flatMap((stop, currentStopIndex) => {
      const stopCommodities = Array.isArray(stop?.commodities)
        ? stop.commodities
        : [];
      return stopCommodities.map((commodity) => ({
        ...commodity,
        _action: commodity._action || "create",
        stopType: stop.stopType || commodity?.type?.name || "",
        stopName:
          stop.consignee?.name ||
          stop.shipper?.name ||
          `Stop ${currentStopIndex + 1}`,
      }));
    }) || [];

  const stopLevelCommodities =
    commodities && commodities.length
      ? commodities
      : Array.isArray(values?.orderStops?.[stopIndex]?.commodities)
        ? values.orderStops[stopIndex].commodities
        : [];

  const commoditiesData = stopLevelCommodities.length
    ? stopLevelCommodities
    : allDataCommodities;

  // Helper function to handle commodity field changes
  const [updatingWeights, setUpdatingWeights] = useState({});
  const { data: servicesTypesData } = useQuery({
    queryKey: ["services"],
    queryFn: () => getServicesActive(),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const serviceTypes = servicesTypesData?.data?.data || [];

  const proccessedServicesTypes = serviceTypes.map((service) => ({
    id: service.uuid,
    label: service.name,
    value: service.uuid, // Store UUID as value for easier matching
  }));

  const { data: commodityTypesData } = useQuery({
    queryKey: ["commodity-types"],
    queryFn: () => getAllCommodityTypes(),
    staleTime: Number.POSITIVE_INFINITY,
  });

  // Flatten all commodities from all stops into a single array with stop info (prefer legs' commodities)
  const allCommodities = useMemo(() => {
    return (
      values?.orderStops?.flatMap((stop, stopIdx) => {
        const list = Array.isArray(stop?.commodities) ? stop.commodities : [];
        return list.map((commodity) => ({
          ...commodity,
          _action: commodity._action || "create",
          stopType: stop.stopType,
          stopName:
            stop.consignee?.name || stop.shipper?.name || `Stop ${stopIdx + 1}`,
        }));
      }) || []
    );
  }, [JSON.stringify(values?.orderStops)]);

  const commodityTypes = commodityTypesData?.data?.data || [];
  const proccessedCommodityTypes = useMemo(() => {
    return commodityTypes.map((commodity) => ({
      id: commodity.uuid,
      label: commodity.name,
      value: commodity.name,
    }));
  }, [commodityTypes]);

  const skidTypeUuid = useMemo(() => {
    return proccessedCommodityTypes.find(
      (type) => type.value.toLowerCase() === "skid",
    )?.id;
  }, [proccessedCommodityTypes]);

  // Replace the handleCommodityChange function with this improved version
  const handleCommodityChange = (index, field, value) => {
    // Normalize empty strings to 0 for dimensional fields
    if (
      ["lengthValue", "width", "height"].includes(field) &&
      (value === "" || value === null || value === undefined)
    ) {
      value = 0;
    }

    // Only check type when the typeUuid field changes
    if (field === "typeUuid") {
      // Allow skid type to be set as default, but show error if user tries to clear an existing type
      if (!value || value === "") {
        const currentCommodity = commoditiesData[index];
        // Only show error if user is trying to clear an existing type (not setting default)
        if (currentCommodity?.typeUuid && !skidTypeUuid) {
          toast.error("Please select a commodity type");
          return;
        }
      }

      const typeName =
        proccessedCommodityTypes &&
        proccessedCommodityTypes?.find((x) => x.id === value)?.value;

      // Also update the typeName field when typeUuid changes
      if (typeName) {
        onChangeCommodity(index, "typeName", typeName);
      }
    }

    // Clear the dim field when dimension-related fields change to force recalculation
    if (["lengthValue", "width", "height", "quantity"].includes(field)) {
      onChangeCommodity(index, "dim", "");
    }

    // Update the commodity in the parent component
    onChangeCommodity(index, field, value);

    // Trigger weight calculation for all commodities, not just bundles
    if (["lengthValue", "width", "quantity"].includes(field) && orderId) {
      // Get the updated commodity with the new value
      const updatedCommodity = {
        ...commoditiesData[index],
        [field]: value,
      };

      // Removed API call to get bundle weight
    }
  };

  // Calculate totals
  const totalQuantity = useMemo(() => {
    return allCommodities.reduce(
      (sum, commodity) => sum + (Number.parseInt(commodity.quantity) || 0),
      0,
    );
  }, [allCommodities]);

  const totalWeight = useMemo(() => {
    return allCommodities.reduce(
      (sum, commodity) => sum + parseFloat(commodity?.weight || 0),
      0,
    );
  }, [allCommodities]);

  const totalDimWeight = useMemo(() => {
    return Math.round(
      allCommodities.reduce((sum, c) => {
        const apiDim = Number.parseFloat(c?.dim);
        if (!isNaN(apiDim) && apiDim > 0) {
          return sum + apiDim;
        }
        const qty = Number.parseFloat(c.quantity) || 0;
        const lg =
          Number.parseFloat(c.lengthValue > 72 ? 96 : c.lengthValue) || 0;
        const wd = Number.parseFloat(c.width > 72 ? 96 : c.width) || 0;
        const ht = Number.parseFloat(c.height > 72 ? 96 : c.height) || 0;
        return sum + (qty * lg * wd * ht) / 172.8;
      }, 0),
    );
  }, [allCommodities]);

  // Initialize commodities with default values when commodityTypes data is loaded
  useEffect(() => {
    if (proccessedCommodityTypes.length > 0) {
      commoditiesData.forEach((commodity, index) => {
        // Set default skid type if no typeUuid is set
        if (!commodity.typeUuid && skidTypeUuid) {
          onChangeCommodity(index, "typeUuid", skidTypeUuid);
          onChangeCommodity(index, "typeName", "skid");
        }

        // Set default descriptions if empty or undefined
        if (!commodity.descriptions || commodity.descriptions.trim() === "") {
          onChangeCommodity(index, "descriptions", "FAK");
        }
      });
    }
  }, [
    proccessedCommodityTypes,
    commoditiesData,
    skidTypeUuid,
    onChangeCommodity,
  ]);

  const serviceMap = useMemo(() => {
    const map = { TL: "", LTL: "" };
    serviceTypes.forEach((s) => {
      const name = s.name?.toLowerCase();
      if (name === "tl") map.TL = s.uuid;
      if (name === "ltl") map.LTL = s.uuid;
    });
    return map;
  }, [serviceTypes]);

  // 2. Auto-switch service when totalDimWeight crosses 10 000
  useEffect(() => {
    if (!serviceMap.TL || !serviceMap.LTL) return; // services not loaded yet

    const desired = totalDimWeight > 10_000 ? serviceMap.TL : serviceMap.LTL;

    if (desired !== serviceUuid) {
      onChangeService(desired);
    }
  }, [totalDimWeight, serviceUuid, serviceMap, onChangeService]);

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom>
        Commodities ({commoditiesData?.length || 0})
      </Typography>
      <Box
        sx={{ display: "flex", flexDirection: "row", alignItems: "flex-end" }}
      >
        {showServiceType && (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="body2"
                  component="span"
                  sx={{ fontWeight: 500 }}
                >
                  Service type{" "}
                  {required && <span style={{ color: "red" }}>*</span>}
                </Typography>
              </Box>
              <CustomSelect
                name="serviceUuid"
                value={serviceUuid?.uuid || serviceUuid || ""}
                onChange={(name, value) => {
                  onChangeService(value); // value is UUID
                }}
                options={proccessedServicesTypes}
                placeholder="Select service type"
                size="small"
                sx={{ background: "#fff" }}
                returnId
                error={touched?.serviceUuids && Boolean(errors?.serviceUuids)}
                errorMessage={
                  touched?.serviceUuids && Boolean(errors?.serviceUuids)
                    ? errors?.serviceUuids
                    : undefined
                }
                inputProps={{ "data-testid": "service-type-select" }}
              />
            </Grid>
          </Grid>
        )}
        {/* Add Commodity Button */}
        <Button
          startIcon={<AddIcon />}
          sx={{
            textTransform: "none",
            color: "primary.main",
            fontWeight: 500,
            width: "fit-content",
            whiteSpace: "nowrap",
            px: 10,
            // "&:hover": {
            //   backgroundColor: "transparent",
            // },
          }}
          onClick={onAddCommodity}
          variant="text"
        >
          Add commodity
        </Button>
      </Box>

      <Box
        sx={{
          mt: 3,
          border: "1px solid #e0e0e0",
          borderRadius: 1,
          overflow: "visible", // Changed from "hidden" to "visible" to allow the X button to show
        }}
      >
        {/* Table Header */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 40px", // Added 40px for the remove button column
            bgcolor: "#f5f5f5",
            p: 1.5,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Description {required && <span style={{ color: "red" }}>*</span>}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, ml: 1 }}>
            QTY {required && <span style={{ color: "red" }}>*</span>}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, ml: 1 }}>
            Type {required && <span style={{ color: "red" }}>*</span>}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, ml: 1 }}>
            LG
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, ml: 1 }}>
            WD
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, ml: 1 }}>
            HT
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, ml: 1 }}>
            PCS
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, ml: 1 }}>
            LF
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, ml: 1 }}>
            Total WT {required && <span style={{ color: "red" }}>*</span>}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, ml: 1 }}>
            Class
          </Typography>
          <Typography variant="body2"></Typography>{" "}
          {/* Empty header for remove button column */}
        </Box>

        {/* Table Rows */}
        {commoditiesData?.map((commodity, index) =>
          // Skip rendering commodities marked for deletion
          !commodity._action || commodity._action !== "delete" ? (
            <Box
              key={index}
              sx={{
                display: "grid",
                gridTemplateColumns:
                  "2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 40px", // Added 40px for the remove button column
                p: 1,
                borderBottom:
                  index < commoditiesData.length - 1
                    ? "1px solid #e0e0e0"
                    : "none",
                alignItems: "center",
                bgcolor: "white",
              }}
            >
              <TextField
                size="small"
                name={`commodities[${index}].descriptions`}
                value={commodity.descriptions || ""}
                onChange={(e) =>
                  handleCommodityChange(index, "descriptions", e.target.value)
                }
                placeholder="FAK"
                sx={{
                  mx: 0.5,
                  background: "#fff",
                }}
                variant="outlined"
                error={
                  touched?.commodities?.[index]?.descriptions &&
                  Boolean(errors?.commodities?.[index]?.descriptions)
                }
              />

              {/* <Autocomplete
                freeSolo
                size="small"
                options={descriptions.map((option) => option.value)}
                value={commodity.descriptions || ""}
                onChange={(event, newValue) => {
                  handleCommodityChange(index, "descriptions", newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Description"
                    variant="outlined"
                    sx={{
                      background: "#fff",
                    }}
                    error={
                      touched?.commodities?.[index]?.descriptions &&
                      Boolean(errors?.commodities?.[index]?.descriptions)
                    }
                  />
                )}
              /> */}
              <TextField
                size="small"
                name={`commodities[${index}].quantity`}
                value={commodity.quantity || ""}
                onChange={(e) =>
                  handleCommodityChange(index, "quantity", e.target.value)
                }
                sx={{
                  mx: 0.5,
                  background: "#fff",
                }}
                variant="outlined"
                error={
                  touched?.commodities?.[index]?.quantity &&
                  Boolean(errors?.commodities?.[index]?.quantity)
                }
              />

              <CustomSelect
                name={`commodities[${index}].typeUuid`}
                value={commodity.typeUuid || skidTypeUuid || ""}
                onChange={(name, value) =>
                  handleCommodityChange(index, "typeUuid", value)
                }
                options={[...proccessedCommodityTypes]}
                placeholder="Type"
                size="small"
                sx={{ background: "#fff" }}
                returnId
                error={
                  touched?.commodities?.[index]?.typeUuid &&
                  Boolean(errors?.commodities?.[index]?.typeUuid)
                }
              />

              <TextField
                size="small"
                name={`commodities[${index}].lengthValue`}
                value={commodity.lengthValue || ""}
                onChange={(e) =>
                  handleCommodityChange(index, "lengthValue", e.target.value)
                }
                placeholder="0 in"
                sx={{
                  mx: 0.5,
                  background: "#fff",
                }}
                variant="outlined"
                error={
                  touched?.commodities?.[index]?.lengthValue &&
                  Boolean(errors?.commodities?.[index]?.lengthValue)
                }
              />

              <TextField
                size="small"
                name={`commodities[${index}].width`}
                value={commodity.width || ""}
                onChange={(e) =>
                  handleCommodityChange(index, "width", e.target.value)
                }
                placeholder="0 in"
                sx={{
                  mx: 0.5,
                  background: "#fff",
                }}
                variant="outlined"
                error={
                  touched?.commodities?.[index]?.width &&
                  Boolean(errors?.commodities?.[index]?.width)
                }
              />

              <TextField
                size="small"
                name={`commodities[${index}].height`}
                value={commodity.height || ""}
                onChange={(e) =>
                  handleCommodityChange(index, "height", e.target.value)
                }
                placeholder="0 in"
                sx={{
                  mx: 0.5,
                  background: "#fff",
                }}
                variant="outlined"
                error={
                  touched?.commodities?.[index]?.height &&
                  Boolean(errors?.commodities?.[index]?.height)
                }
              />

              <TextField
                size="small"
                name={`commodities[${index}].pieces`}
                value={commodity.pieces || ""}
                onChange={(e) =>
                  handleCommodityChange(index, "pieces", e.target.value)
                }
                placeholder="PCS"
                sx={{
                  mx: 0.5,
                  background: "#fff",
                }}
                variant="outlined"
                error={
                  touched?.commodities?.[index]?.pieces &&
                  Boolean(errors?.commodities?.[index]?.pieces)
                }
              />

              <TextField
                size="small"
                name={`commodities[${index}].lf`}
                value={commodity.lf || ""}
                onChange={(e) =>
                  handleCommodityChange(index, "lf", e.target.value)
                }
                placeholder="lf"
                sx={{
                  mx: 0.5,
                  background: "#fff",
                }}
                variant="outlined"
                error={
                  touched?.commodities?.[index]?.lf &&
                  Boolean(errors?.commodities?.[index]?.lf)
                }
              />
              {updatingWeights[index] ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "40px",
                  }}
                >
                  <FaSpinner className="text-[#1e55bf] w-5 h-5 animate-spin" />
                </Box>
              ) : (
                <TextField
                  size="small"
                  name={`commodities[${index}].weight`}
                  value={commodity.weight || ""}
                  onChange={(e) =>
                    handleCommodityChange(index, "weight", e.target.value)
                  }
                  placeholder="0 lbs"
                  sx={{
                    mx: 0.5,
                    background: "#fff",
                  }}
                  variant="outlined"
                  error={
                    touched?.commodities?.[index]?.weight &&
                    Boolean(errors?.commodities?.[index]?.weight)
                  }
                />
              )}

              <CustomSelect
                name={`commodities[${index}].class`}
                value={commodity.class || ""}
                onChange={(name, value) =>
                  handleCommodityChange(index, "class", value)
                }
                options={[
                  { id: "", value: "", label: "None" },
                  ...classes.map((cls) => ({
                    id: cls,
                    value: cls,
                    label: cls,
                  })),
                ]}
                placeholder="None"
                size="small"
                sx={{ background: "#fff" }}
                error={
                  touched?.commodities?.[index]?.class &&
                  Boolean(errors?.commodities?.[index]?.class)
                }
              />

              {/* Remove button - now in its own column */}
              <IconButton
                size="small"
                color="error"
                onClick={() => onRemoveCommodity(index)}
                sx={{
                  padding: 0.5,
                  minWidth: "auto",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : null,
        )}

        {commoditiesData.filter((c) => !c._action || c._action !== "delete")
          .length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                "2fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 40px", // Added 40px for consistency with other rows
              p: 1,
              backgroundColor: "#fafafa",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, ml: 2 }}>
              Totals
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, ml: 2 }}>
              {totalQuantity}
            </Typography>
            <Typography variant="body2"></Typography>
            <Typography variant="body2"></Typography>
            <Typography variant="body2"></Typography>
            <Typography variant="body2"></Typography>
            <Typography variant="body2"></Typography>
            <Typography variant="body2"></Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {totalWeight} lbs
              <br />
              {totalDimWeight} lbs (DIM)
              <Tooltip title="Dimensional weight calculation">
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{
                    ml: 0.5,
                    color: "primary.main",
                    fontSize: "16px",
                    verticalAlign: "middle",
                  }}
                />
              </Tooltip>
            </Typography>
            <Typography variant="body2"></Typography>
            <Typography variant="body2"></Typography>{" "}
            {/* Empty cell for alignment with remove button column */}
          </Box>
        ) : null}
      </Box>
      <Typography
        sx={{
          fontSize: 12,
          textAlign: "right",
          fontStyle: "italic",
          fontWeight: "bold",
          color: "error.main",
        }}
      >
        * Note: DIM weight may change based on tariff cubic density.
      </Typography>
    </Box>
  );
};

export default CommoditiesTable;
